const round0 = (n) => Math.round(Number(n) || 0)
const formatWholeCurrency = (n) => `$${round0(n).toLocaleString('en-US')}`

const flavorMoney = (flavors, taxRate) => (flavors || []).map((f) => ({
  id: f.id,
  name: f.name,
  recommended: !!f.recommended,
  rationale: f.rationale || '',
  bestFor: f.bestFor || '',
  monthly: round0(f.monthly),
  annual: round0(f.annual != null ? f.annual : (Number(f.monthly) || 0) * 12),
  startupOneTime: round0(f.startupOneTime || 0),
  startupItems: Array.isArray(f.startupItems)
    ? f.startupItems.map((it) => ({ key: it.key, label: it.label || '', price: round0(it.price || 0) }))
    : [],
  tax: round0((Number(f.monthly) || 0) * ((Number(taxRate) || 0) / 100)),
}))

export function hasEstimateContract (contract) {
  return !!(contract && Array.isArray(contract.flavors) && contract.flavors.length)
}

export function buildScopeData (contract) {
  const { services = [], flavors = [], taxable, taxRate, scopeNote } = contract || {}
  return {
    headline: (flavors || []).length === 1 ? 'Your Service Plan' : 'Choose Your Service Plan',
    note: scopeNote || '',
    taxable: !!taxable,
    taxRate: Number(taxRate) || 0,
    flavors: flavorMoney(flavors, taxRate),
    services: services.map((svc) => ({
      name: svc.name,
      desc: svc.shortDescription || '',
      cells: flavors.map((f) => {
        const entry = (svc.byFlavor && svc.byFlavor[f.id]) || { mode: 'off' }
        return {
          mode: entry.mode || 'off',
          freq: entry.freq || '',
          tag: entry.tag || entry.detail || '',
        }
      }),
    })),
  }
}

export function buildProjectData (contract) {
  const { flavors = [], terms = {}, client = {}, taxable, taxRate } = contract || {}
  const projectFlavors = flavorMoney(flavors, taxRate)
  const selectedFlavor = projectFlavors.find((f) => f.recommended) || projectFlavors[0] || null
  return {
    headline: 'Project Details',
    taxable: !!taxable,
    taxRate: Number(taxRate) || 0,
    flavors: projectFlavors,
    details: [
      { label: 'Contract Term', value: `${terms.termMonths || 12} Months` },
      { label: 'Service Frequency', value: terms.frequencyLabel || '' },
      ...(selectedFlavor
        ? [{ label: 'Monthly Maintenance Fee', value: `${formatWholeCurrency(selectedFlavor.monthly)} / mo` }]
        : []),
      { label: 'Payment Terms', value: '{{paymentTerms}}' },
      { label: 'Prepared For', value: client.contact || client.name || '' },
    ],
    taxLine: taxable
      ? `Estimated sales tax shown at ${taxRate}% and added to the monthly fee. Final tax applied per jurisdiction at invoicing.`
      : 'Totals do not include applicable taxes & fees.',
  }
}

// Estimate-driven pieces of the Executive Summary: property snapshot rows and
// the recommended-plan card. Static content (guarantees/stats/split lists)
// comes from the module-registry defaults.
export function buildExecSummaryData (contract) {
  const { flavors = [], services = [], takeoffSummary = {}, property = {}, client = {}, taxRate } = contract || {}
  const rec = flavors.find((f) => f.recommended) || flavors[0] || null
  const money = rec ? flavorMoney([rec], taxRate)[0] : null

  const items = rec
    ? services
      .map((svc) => ({ svc, entry: svc.byFlavor && svc.byFlavor[rec.id] }))
      .filter((x) => x.entry && x.entry.mode === 'included')
      .slice(0, 3)
      .map((x) => `${x.svc.name}${x.entry.freq ? ` — ${x.entry.freq}` : ''}`)
    : []

  const sqft = property.sqft != null ? property.sqft : takeoffSummary.sqft
  const areas = property.areas != null ? property.areas : takeoffSummary.areas
  const rows = []
  if (sqft) rows.push({ label: 'Mapped area', value: `${Number(sqft).toLocaleString('en-US')} SF` })
  if (areas) rows.push({ label: 'Service areas', value: String(areas) })
  if (client.date) rows.push({ label: 'Proposal date', value: client.date })

  return {
    property: {
      name: property.name || client.name || '',
      address: [property.address, property.city, property.state].filter(Boolean).join(', '),
      rows,
    },
    recommended: money
      ? {
          name: money.name,
          monthly: money.monthly,
          annual: money.annual,
          startupOneTime: money.startupOneTime,
          items,
          // With a single tier there is no comparison to recommend against.
          single: flavors.length === 1,
        }
      : null,
  }
}

export function buildEstimateVariables (contract) {
  const rates = (contract && contract.rates) || {}
  const terms = (contract && contract.terms) || {}
  const out = {}
  if (rates.laborer != null) out.laborerRate = rates.laborer
  if (rates.irrigation != null) out.irrigationRate = rates.irrigation
  if (rates.sprayTech != null) out.sprayTechRate = rates.sprayTech
  if (rates.operator != null) out.operatorRate = rates.operator
  if (rates.craneOperator != null) out.craneOperatorRate = rates.craneOperator
  if (rates.emergency != null) out.emergencyRate = rates.emergency
  if (terms.paymentTerms) out.paymentTerms = terms.paymentTerms
  return out
}

export function mergeEstimateVariables (baseVariables, estimateVariables, proposalVariables = {}) {
  const merged = { ...(baseVariables || {}) }
  Object.entries(estimateVariables || {}).forEach(([key, value]) => {
    if (key === 'paymentTerms' && Object.prototype.hasOwnProperty.call(proposalVariables, key)) return
    merged[key] = value
  })
  return merged
}

export function activeDetailServices (contract) {
  const { services = [] } = contract || {}
  const detailNamesByKey = {
    maintenance: ['Basic Maintenance', 'Trimming', 'Mowing', 'Mowing (if applicable)'],
    maint: ['Basic Maintenance', 'Trimming', 'Mowing', 'Mowing (if applicable)'],
    irrigation: ['Irrigation'],
    herbicide: ['Weed Control & Pre-Emergent'],
    arborist: ['Arbor Care'],
    fert: [],
    fertilization: [],
  }

  return services
    .map((svc) => {
      const modes = Object.values(svc.byFlavor || {}).map((entry) => entry && entry.mode)
      const key = svc.key || svc.role || svc.name
      const detailNames = detailNamesByKey[String(key || '').toLowerCase()] || [svc.name]
      if (modes.includes('included')) return { name: svc.name, badge: 'included', detailNames }
      if (modes.includes('optional')) return { name: svc.name, badge: 'additional', detailNames }
      return null
    })
    .filter(Boolean)
}
